import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Upload, Check, Clock, X, LogOut, ChevronRight, ChevronLeft,
  CheckCircle, AlertTriangle, Trash2, Eye, Briefcase,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api, { uploadRequestConfig, uploadErrorMessage } from '../../services/api';
import { MAX_FILE_MB } from '../../config/config';
import LoadingSpinner from '../common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { sectionsForEmploymentType, allCandidateFields, Field } from './CandidateFormFields';

const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

const STEPS = [
  { key: 'documents', label: 'Documents' },
  { key: 'details', label: 'Your Details' },
  { key: 'review', label: 'Confirm & Submit' },
];

const statusBanner = {
  submitted: {
    icon: Clock,
    tone: 'bg-blue-50 border-blue-200 text-blue-800',
    title: 'Your onboarding form has been submitted',
    body: 'The HR team is reviewing your details and documents. You will be notified once it is processed.',
  },
  pending: {
    icon: Clock,
    tone: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    title: 'Your onboarding is with the approving authority',
    body: 'HR has reviewed your submission and sent it for final approval. Nothing further is needed from you.',
  },
  rejected: {
    icon: X,
    tone: 'bg-red-50 border-red-200 text-red-800',
    title: 'Your onboarding request was rejected',
    body: 'Please get in touch with the HR team for next steps.',
  },
};

const CandidateOnboarding = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [step, setStep] = useState(0);

  const [status, setStatus] = useState(null);
  const [employmentType, setEmploymentType] = useState('fresher');
  const [checklist, setChecklist] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [rejectionReason, setRejectionReason] = useState(null);
  const [form, setForm] = useState({});

  const editable = status === 'invited';

  const load = useCallback(async () => {
    try {
      const res = await api.get('/onboarding/me');
      const { candidate, checklist: list, documents: docs, employmentType: type } = res.data;

      setStatus(candidate.onboardingStatus);
      setEmploymentType(type || 'fresher');
      setChecklist(list || []);
      setDocuments(docs || []);
      setRejectionReason(candidate.onboardingRejectionReason || null);

      const next = {};
      allCandidateFields(type || 'fresher').forEach(({ name, type: fieldType }) => {
        const raw = candidate[name];
        if (fieldType === 'date') next[name] = toDateInput(raw);
        else if (fieldType === 'checkbox') next[name] = !!raw;
        else if (raw !== undefined && raw !== null) next[name] = raw;
        else if (name === 'candidateDeclarationDate') next[name] = new Date().toISOString().split('T')[0];
        else next[name] = '';
      });
      setForm(next);
    } catch (error) {
      console.error('Load onboarding error:', error);
      toast.error(error.response?.data?.message || 'Failed to load your onboarding form');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFieldChange = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const saveDetails = async ({ silent = false } = {}) => {
    if (!editable) return true;
    setSaving(true);
    try {
      await api.put('/onboarding/me', form);
      if (!silent) toast.success('Your details have been saved');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save your details');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (documentType, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${MAX_FILE_MB} MB.`);
      return;
    }

    setUploadingType(documentType);
    setUploadProgress(0);
    const data = new FormData();
    data.append('file', file);
    data.append('documentType', documentType);

    try {
      await api.post('/documents/me/upload', data, uploadRequestConfig(setUploadProgress));
      const res = await api.get('/documents/me');
      setDocuments(res.data.documents || []);
      toast.success('Document uploaded');
    } catch (error) {
      toast.error(uploadErrorMessage(error));
    } finally {
      setUploadingType(null);
      setUploadProgress(null);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await api.delete(`/documents/me/${documentId}`);
      setDocuments((prev) => prev.filter((d) => d._id !== documentId));
      toast.success('Document removed');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove document');
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      const res = await api.get(`/documents/me/${documentId}/access-url`);
      if (res.data?.accessUrl) window.open(res.data.accessUrl, '_blank', 'noopener');
    } catch (error) {
      toast.error('Failed to open document');
    }
  };

  const handleSubmit = async () => {
    const saved = await saveDetails({ silent: true });
    if (!saved) return;

    setSubmitting(true);
    try {
      const res = await api.post('/onboarding/me/submit');
      setStatus(res.data.candidate.onboardingStatus);
      toast.success('Your onboarding form has been submitted');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit your onboarding form');
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = async (index) => {
    if (index > step && step === 1) {
      const ok = await saveDetails({ silent: true });
      if (!ok) return;
    }
    setStep(index);
  };

  if (loading) return <LoadingSpinner />;

  const docFor = (type) => documents.find((d) => d.documentType === type);
  const missingDocs = checklist.filter((doc) => !docFor(doc.value));
  const sections = sectionsForEmploymentType(employmentType);
  const missingRequiredFields = allCandidateFields(employmentType)
    .filter((f) => f.required && (f.type === 'checkbox' ? !form[f.name] : !String(form[f.name] ?? '').trim()))
    .map((f) => f.label);

  const banner = statusBanner[status];

  const header = (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Briefcase className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Candidate Onboarding</h1>
            <p className="text-xs text-gray-500">{user?.name} · {user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );

  // Already submitted / forwarded / rejected: read-only status screen
  if (banner) {
    const Icon = banner.icon;
    return (
      <div className="min-h-screen bg-gray-50">
        {header}
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className={`rounded-xl border p-6 ${banner.tone}`}>
            <div className="flex items-start gap-4">
              <Icon className="h-6 w-6 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold">{banner.title}</h2>
                <p className="mt-1 text-sm">{banner.body}</p>
                {status === 'rejected' && rejectionReason && (
                  <p className="mt-3 text-sm"><strong>Reason:</strong> {rejectionReason}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {header}

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Stepper */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, index) => (
            <React.Fragment key={s.key}>
              <button
                onClick={() => goToStep(index)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  index === step
                    ? 'bg-primary-600 text-white border-primary-600'
                    : index < step
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-white text-gray-500 border-gray-200'
                }`}
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-xs">
                  {index < step ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                {s.label}
              </button>
              {index < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-gray-300" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1 - Documents */}
        {step === 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Required Documents</h2>
              <p className="text-sm text-gray-500 mt-1">
                Showing the checklist for a <strong className="capitalize">{employmentType}</strong> candidate.
                PDF or image files, up to {MAX_FILE_MB} MB each.
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {checklist.map((doc) => {
                const uploaded = docFor(doc.value);
                return (
                  <div key={doc.value} className="px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <FileText className={`h-5 w-5 mt-0.5 flex-shrink-0 ${uploaded ? 'text-green-600' : 'text-gray-400'}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{doc.label}</p>
                        {uploaded ? (
                          <p className="text-xs text-green-600 truncate">Uploaded: {uploaded.originalFileName}</p>
                        ) : (
                          <p className="text-xs text-gray-400">Not uploaded yet</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {uploaded && (
                        <>
                          <button
                            onClick={() => handleViewDocument(uploaded._id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            <Eye className="h-3.5 w-3.5" /> View
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(uploaded._id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Remove
                          </button>
                        </>
                      )}
                      <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 cursor-pointer">
                        <Upload className="h-3.5 w-3.5" />
                        {uploadingType === doc.value
                          ? uploadProgress === null
                            ? 'Uploading…'
                            : `Uploading… ${uploadProgress}%`
                          : uploaded
                            ? 'Replace'
                            : 'Upload'}
                        <input
                          type="file"
                          className="sr-only"
                          accept="application/pdf,image/jpeg,image/png"
                          disabled={uploadingType === doc.value}
                          onChange={(e) => {
                            handleUpload(doc.value, e.target.files?.[0]);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => goToStep(1)}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
              >
                Continue <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2 - Details */}
        {step === 1 && (
          <div className="space-y-6">
            {sections.map((section) => (
              <div key={section.title} className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">{section.title}</h2>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {section.fields.map((field) => (
                    <Field
                      key={field.name}
                      field={field}
                      value={form[field.name]}
                      onChange={handleFieldChange}
                      disabled={!editable}
                    />
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-between">
              <button
                onClick={() => goToStep(0)}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => saveDetails()}
                  disabled={saving}
                  className="px-5 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save draft'}
                </button>
                <button
                  onClick={() => goToStep(2)}
                  className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                >
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3 - Review & submit */}
        {step === 2 && (
          <div className="space-y-6">
            {(missingDocs.length > 0 || missingRequiredFields.length > 0) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold">Some things still need your attention</p>
                    {missingRequiredFields.length > 0 && (
                      <p className="mt-1">Missing details: {missingRequiredFields.join(', ')}</p>
                    )}
                    {missingDocs.length > 0 && (
                      <p className="mt-1">Missing documents: {missingDocs.map((d) => d.label).join(', ')}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Documents</h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {checklist.map((doc) => {
                  const uploaded = docFor(doc.value);
                  return (
                    <div key={doc.value} className="flex items-center gap-2 text-sm">
                      {uploaded ? (
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                      )}
                      <span className="text-gray-700">{doc.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {sections.map((section) => (
              <div key={section.title} className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">{section.title}</h2>
                </div>
                <dl className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  {section.fields.map((field) => {
                    const value = form[field.name];
                    const display =
                      field.type === 'checkbox'
                        ? (value ? 'Yes' : 'No')
                        : (value === '' || value === null || value === undefined ? '—' : String(value));
                    return (
                      <div key={field.name} className="flex justify-between gap-4 border-b border-gray-50 pb-2">
                        <dt className="text-sm text-gray-500">{field.label}</dt>
                        <dd className="text-sm font-medium text-gray-900 text-right break-words">{display}</dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            ))}

            <div className="flex justify-between">
              <button
                onClick={() => goToStep(1)}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" /> Back to details
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 text-sm font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                {submitting ? 'Submitting…' : 'Confirm & Submit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateOnboarding;
